<template>
    <div class="dd-rule-value-row">
        <DsSpinner v-if="loadingStatus === $options.LOADING && !isHidden" :size="12" />

        <DsInlineAlert v-else-if="loadingStatus === $options.ERROR && !isHidden" leading-icon type="critical">
            <template #default>
                {{ $t('funnel.edit.decision.error.loading.rule.value') }}
            </template>
        </DsInlineAlert>

        <div v-else-if="loadingStatus === $options.SUCCESS && !isHidden" class="dd-rule-value-row">
            <div v-if="index !== 1" class="comparison-label">
                {{ ruleObject.comparison }}
            </div>

            <DsDatePicker
                v-if="isDatePicker"
                v-model="selectedDatePicker"
                date-only
                :label="$t('funnel.edit.decision.category.select.label')"
                class="ds-datepicker-wrapper"
                @input="onDatePickerInput"
            />
            <DsInputField
                v-if="isInputTextField"
                v-model="selectedInputTextField"
                class="ds-input-field-wrapper"
                :placeholder="$t('funnel.edit.decision.category.select.label')"
                @blur="onBlurSaveInputTextField"
            >
                <template #trailing>
                    <span
                        class="icon-x-delete-rule-value-input"
                        @click="deleteRuleValueEvent"
                    >
                        <DsIcon name="minus-circle" />
                    </span>
                </template>
            </DsInputField>

            <div v-if="isMultiselectInputField" class="multiselect-with-alert" @click.stop>
                <DsMultiselect
                    :value="multiselectInputFieldData"
                    searchable
                    :loading="isTagPicker && tagSearchLoading"
                    :placeholder="$t('funnel.edit.decision.category.select.label')"
                    class="ds-multiselect-wrapper"
                    :options="multiselectInputFieldOptions"
                    @input="onMultiselectInputField"
                    @updateSearch="onTagSearch"
                    @blur="onTagDropdownClose"
                    @click.native="onTagDropdownFocus"
                    @focusin.native="onTagDropdownFocus"
                >
                    <template #option="{ option, isSelected, filter }">
                        <div class="menu-item" :class="[{ selected: isSelected }]">
                            <span v-if="option.groupLabel" class="group-label">{{ option.groupLabel }}</span>
                            <span v-else-if="isTagPicker" class="option-label"><DsTextHighlighter :content="option.label" :highlight="filter" /></span>
                            <span v-else class="option-label">{{ option.label }}</span>
                        </div>
                    </template>
                </DsMultiselect>

                <DsInlineAlert
                    v-if="tagAlert"
                    leading-icon
                    class="tag-alert"
                    :type="tagAlert.type"
                    :data-qa="tagAlert.dataQa"
                >
                    {{ tagAlert.message }}
                </DsInlineAlert>
            </div>

            <DsIconButton
                v-if="isMultiselectInputField && !isFirstElement"
                name="x-circle-fill"
                data-qa="delete-rule-value"
                :aria-label="$t('funnel.edit.decision.delete.rule.value')"
                @mousedown.native.stop.prevent="preventBlurBeforeClick"
                @click="deleteRuleValueEvent"
            />

            <div v-if="index === numRuleValues && (mayOrValue || mayAndValue)" class="may-or-and-wrapper">
                <span v-if="showOrValues" class="may-or-label" @click="addRuleValueEvent(COMPARISON_OPTIONS.OR)">
                    {{ $t('funnel.edit.decision.plus.or.label') }}
                </span>
                <span v-if="showAndValues" class="may-and-label" @click="addRuleValueEvent(COMPARISON_OPTIONS.AND)">
                    {{ $t('funnel.edit.decision.plus.and.label') }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export const COMPARISON_OPTIONS = {
    OR: 'Or',
    AND: 'And',
};

import debounce from 'lodash.debounce';
import {
    getInputAttributes,
    getAllUsers,
    addRuleValue,
    deleteRuleValue,
    searchTags,
    getTagsByIds,
} from './decision.api';
import {
    LOADING, IDLE, SUCCESS, ERROR,
} from '@/components/shared/constants/loadingStatuses.constants';
import {
    convertFromDpToString,
    convertFromStringToDp,
    getTagsList,
    INPUT_TYPE_HIDDEN,
    INPUT_TYPE_AUTOCOMPLETE,
    INPUT_TYPE_DATE,
    INPUT_TYPE_CITY,
    INPUT_TYPE_STRING,
    INPUT_TYPE_NAME,
    INPUT_TYPE_EMAIL,
    INPUT_TYPE_PHONE,
    INPUT_TYPE_STATE,
    INPUT_TYPE_URL,
    INPUT_TYPE_COUNTRY,
    INPUT_TYPE_EMAIL_STATUS,
    INPUT_TYPE_SELECT_LANGUAGE_CODE,
    INPUT_TYPE_LEADSOURCE,
    INPUT_TYPE_CONTACT_TYPE,
    INPUT_TYPE_CONTACT_TITLE,
    INPUT_TYPE_USERS,
    INPUT_TYPE_CURRENCY,
    INPUT_DAY_OF_WEEK,
    INPUT_SELECT_WITH_DEFAULT,
    INPUT_DOUBLE,
    INPUT_INTEGER,
    INPUT_PERCENT,
    INPUT_DRILL_DOWN,
    INPUT_MONTH,
    INPUT_CUSTOM_EMAIL,
    INPUT_YEAR,
    INPUT_STATE_SELECT,
    INPUT_TEXT_AREA,
    INPUT_DATETIME,
    TAGS_FIELD_KEY,
} from './decisionDiamond.util';
import { SEARCHING_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';

export const TAG_SEARCH_LIMIT = 25;

export default {
    LOADING,
    SUCCESS,
    ERROR,
    props: {
        ruleObject: {
            type: Object,
            required: true,
        },
        ruleValue: {
            type: Object,
            required: true,
        },
        ruleMapKey: {
            type: String,
            required: true,
        },
        valueMapKey: {
            type: Number,
            required: true,
        },
        isFirstElement: {
            type: Boolean,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
        numRuleValues: {
            type: Number,
            required: true,
        },
        ddKeys: {
            type: Object,
            required: true,
        },
        inputTypeValue: {
            type: String,
            required: true,
        },
        mayOrValue: {
            type: Boolean,
            required: false,
        },
        mayAndValue: {
            type: Boolean,
            required: false,
        },
    },
    emits: ['update-rule-value', 'delete-rule-value', 'add-rule-value'],
    data() {
        return {
            multiselectInputFieldData: null,
            multiselectInputFieldOptions: [],
            selectedDatePicker: '',
            selectedInputTextField: '',
            comparison: '',
            loadingStatus: IDLE,
            fieldKey: '',
            tagSearchLoading: false,
            tagSearchError: false,
            unresolvedTagId: null,
            COMPARISON_OPTIONS,
        };
    },
    computed: {
        isHidden() {
            return this.inputTypeValue === INPUT_TYPE_HIDDEN;
        },
        isDatePicker() {
            return this.inputTypeValue === INPUT_TYPE_DATE
                || this.inputTypeValue === INPUT_DATETIME;
        },
        isInputTextField() {
            return this.inputTypeValue === INPUT_TYPE_CITY
                || this.inputTypeValue === INPUT_TYPE_STRING
                || this.inputTypeValue === INPUT_TYPE_NAME
                || this.inputTypeValue === INPUT_TYPE_EMAIL
                || this.inputTypeValue === INPUT_TYPE_CURRENCY
                || this.inputTypeValue === INPUT_TYPE_PHONE
                || this.inputTypeValue === INPUT_TYPE_STATE
                || this.inputTypeValue === INPUT_TYPE_URL
                || this.inputTypeValue === INPUT_DOUBLE
                || this.inputTypeValue === INPUT_INTEGER
                || this.inputTypeValue === INPUT_PERCENT
                || this.inputTypeValue === INPUT_CUSTOM_EMAIL
                || this.inputTypeValue === INPUT_YEAR
                || this.inputTypeValue === INPUT_TEXT_AREA;
        },
        isMultiselectInputField() {
            return this.inputTypeValue === INPUT_TYPE_COUNTRY
                || this.inputTypeValue === INPUT_TYPE_EMAIL_STATUS
                || this.inputTypeValue === INPUT_TYPE_SELECT_LANGUAGE_CODE
                || this.inputTypeValue === INPUT_TYPE_LEADSOURCE
                || this.inputTypeValue === INPUT_TYPE_USERS
                || this.inputTypeValue === INPUT_TYPE_CONTACT_TYPE
                || this.inputTypeValue === INPUT_TYPE_CONTACT_TITLE
                || this.inputTypeValue === INPUT_TYPE_AUTOCOMPLETE
                || this.inputTypeValue === INPUT_DAY_OF_WEEK
                || this.inputTypeValue === INPUT_SELECT_WITH_DEFAULT
                || this.inputTypeValue === INPUT_DRILL_DOWN
                || this.inputTypeValue === INPUT_MONTH
                || this.inputTypeValue === INPUT_STATE_SELECT;
        },
        showOrValues() {
            return this.mayOrValue && (this.comparison === COMPARISON_OPTIONS.OR || this.numRuleValues === 1);
        },
        showAndValues() {
            return this.mayAndValue && (this.comparison === COMPARISON_OPTIONS.AND || this.numRuleValues === 1);
        },
        isTagPicker() {
            return this.fieldKey === TAGS_FIELD_KEY && this.isMultiselectInputField;
        },

        tagAlert() {
            if (!this.isTagPicker) {
                return null;
            }

            if (this.tagSearchError) {
                return {
                    type: 'critical',
                    dataQa: 'tag-search-error',
                    message: this.$t('funnel.edit.decision.error.loading.tags'),
                };
            }

            if (this.unresolvedTagId) {
                return {
                    type: 'warning',
                    dataQa: 'tag-unresolved',
                    message: this.$t('funnel.edit.decision.tag.unresolved'),
                };
            }

            return null;
        },
    },
    created() {
        this.tagOptionsLoaded = false;
        this.tagDropdownOpen = false;
        this.tagRequestId = 0;
        this.tagFirstPageOptions = [];
        this.isDestroyed = false;
        this.debouncedFetchTagOptions = debounce(this.fetchTagOptions, SEARCHING_DEBOUNCE_DELAY);
    },
    beforeDestroy() {
        this.isDestroyed = true;
        this.tagRequestId++;
        this.debouncedFetchTagOptions.cancel();
    },
    async mounted() {
        if (!this.isHidden) {
            await this.loadInputAttributes();
        }
    },
    methods: {
        async loadInputAttributes() {
            this.loadingStatus = LOADING;

            try {
                await this.initializeInputAttributes();

                if (this.isDestroyed) {
                    return;
                }

                this.loadingStatus = SUCCESS;
            } catch (error) {
                if (this.isDestroyed) {
                    return;
                }

                this.loadingStatus = ERROR;
            }
        },
        async onBlurSaveInputTextField() {
            this.$emit('update-rule-value', {
                ruleValue: {
                    id: this.valueMapKey,
                    ruleId: this.ruleValue.ruleId,
                    value: this.selectedInputTextField,
                },
            });
        },
        onMultiselectInputField(value) {
            this.tagDropdownOpen = false;

            if (!value || !value.value) {
                return;
            }

            this.multiselectInputFieldData = value;
            this.unresolvedTagId = null;

            this.$emit('update-rule-value', {
                ruleValue: {
                    ...this.ruleValue,
                    value: this.multiselectInputFieldData.value,
                    id: this.valueMapKey,
                },
            });
        },
        preventBlurBeforeClick() {},
        async deleteRuleValueEvent() {
            const ruleValueId = this.valueMapKey;
            const { rule } = await deleteRuleValue(ruleValueId, this.ddKeys);

            this.$emit('delete-rule-value', rule);
            this.selectedInputTextField = '';
            this.multiselectInputFieldData = null;
            this.unresolvedTagId = null;
        },
        async addRuleValueEvent(comparisonType) {
            const payload = {
                comparison: comparisonType,
                ...this.ddKeys,
            };
            const { ruleId } = this.ruleValue;

            const { rule } = await addRuleValue(ruleId, payload);

            this.$emit('add-rule-value', rule);
        },
        onDatePickerInput() {
            if (this.selectedDatePicker) {
                this.$emit('update-rule-value', {
                    ruleValue: {
                        ...this.ruleValue,
                        value: convertFromDpToString(this.selectedDatePicker),
                        id: this.valueMapKey,
                    },
                });
            }
        },

        async initializeTagField() {
            this.multiselectInputFieldOptions = [];
            this.unresolvedTagId = null;

            const selectedTagId = this.ruleValue.value;

            if (!selectedTagId) {
                return;
            }

            const { map = {} } = await getTagsByIds([selectedTagId]);

            if (this.isDestroyed) {
                return;
            }

            const rawTag = map[selectedTagId];

            if (!rawTag) {
                this.unresolvedTagId = selectedTagId;

                return;
            }

            const [selectedTag] = getTagsList({ [selectedTagId]: rawTag });

            this.multiselectInputFieldData = {
                value: selectedTag.value,
                label: selectedTag.label,
            };
        },

        withSelectedTagOption(options) {
            const selected = this.multiselectInputFieldData;

            if (!selected || options.some((option) => option.value === selected.value)) {
                return options;
            }

            return [selected, ...options];
        },

        matchesTagSearch(label, query) {
            if (!query) {
                return true;
            }

            return label.toLowerCase().includes(query.toLowerCase());
        },
        async fetchTagOptions(query) {
            const requestId = ++this.tagRequestId;

            this.tagSearchLoading = true;
            this.tagSearchError = false;

            try {
                const { map = {} } = await searchTags({ query, limit: TAG_SEARCH_LIMIT });

                if (requestId !== this.tagRequestId) {
                    return;
                }

                const options = getTagsList(map)
                    .map((tag) => ({
                        value: tag.value,
                        label: tag.label,
                    }))
                    .filter((option) => this.matchesTagSearch(option.label, query));

                if (!query) {
                    this.tagFirstPageOptions = options;
                }

                this.multiselectInputFieldOptions = query
                    ? options
                    : this.withSelectedTagOption(options);
            } catch (error) {
                if (requestId === this.tagRequestId) {
                    this.tagSearchError = true;
                    this.tagOptionsLoaded = false;
                }
            } finally {
                if (requestId === this.tagRequestId) {
                    this.tagSearchLoading = false;
                }
            }
        },

        onTagSearch(query) {
            if (!this.isTagPicker) {
                return;
            }

            if (!this.tagDropdownOpen) {
                this.debouncedFetchTagOptions.cancel();

                return;
            }

            this.debouncedFetchTagOptions(query);
        },

        onTagDropdownFocus() {
            if (!this.isTagPicker || this.tagDropdownOpen) {
                return;
            }

            this.tagDropdownOpen = true;

            if (this.tagOptionsLoaded) {
                return;
            }

            this.tagOptionsLoaded = true;
            this.fetchTagOptions('');
        },

        onTagDropdownClose() {
            this.tagDropdownOpen = false;
            this.debouncedFetchTagOptions.cancel();

            this.tagRequestId++;
            this.tagSearchLoading = false;
            this.tagSearchError = false;

            if (this.tagFirstPageOptions.length) {
                this.multiselectInputFieldOptions = this.withSelectedTagOption(this.tagFirstPageOptions);

                return;
            }

            this.tagOptionsLoaded = false;
            this.multiselectInputFieldOptions = [];
        },

        async initializeInputAttributes() {
            const {
                field,
                comparison,
            } = this.ruleObject;

            this.comparison = comparison;
            this.fieldKey = field;

            if (this.isTagPicker) {
                await this.initializeTagField();

                return;
            }

            const { value: valueField, ruleId } = this.ruleValue;

            const fieldId = `value${this.ruleMapKey}_${ruleId}`;
            const fieldValue = valueField;
            const fieldKey = field;
            const { map } = await getInputAttributes({
                fieldId,
                fieldValue,
                fieldKey,
                ...this.ddKeys,
            });

            const {
                selectedValue,
                items = {},
                objectType,
                firstOptions,
                drillDownOptions,
            } = map;

            if (Object.keys(items).length > 0) {
                const { selectOptions } = items;

                if (selectOptions) {
                    this.multiselectInputFieldOptions = Object.entries(selectOptions).map(([key, text]) => ({
                        value: key,
                        label: text,
                    }));

                    if (this.ruleValue.value) {
                        this.multiselectInputFieldData = this.multiselectInputFieldOptions.find((v) => v.value === this.ruleValue.value) || null;
                    }
                } else {
                    this.multiselectInputFieldOptions = Object.entries(items).map(([key, text]) => ({
                        value: key,
                        label: text,
                    }));

                    if (this.ruleValue.value) {
                        this.multiselectInputFieldData = this.multiselectInputFieldOptions.find((v) => v.value === this.ruleValue.value) || null;
                    }
                }
            } else if (this.isDatePicker) {
                this.selectedDatePicker = (selectedValue === 'null' || selectedValue == null) ? '' : convertFromStringToDp(selectedValue);
            } else if (this.isInputTextField) {
                this.selectedInputTextField = (selectedValue === 'null' || selectedValue == null) ? '' : selectedValue;
            } else if (this.isMultiselectInputField) {
                if (objectType === 'drilldownOption') {
                    this.multiselectInputFieldOptions = Object.keys(firstOptions).map((key) => {
                        const options = Object.keys(drillDownOptions[key]).map((optionKey) => ({
                            value: optionKey,
                            label: drillDownOptions[key][optionKey],
                        }));

                        return {
                            label: firstOptions[key],
                            options,
                        };
                    });

                    if (this.ruleValue.value && this.multiselectInputFieldOptions.length > 0) {
                        const foundOption = this.multiselectInputFieldOptions.reduce((acc, group) => {
                            if (acc) return acc;
                            const option = group.options.find((opt) => opt.value === this.ruleValue.value);

                            return option || acc;
                        }, null);

                        this.multiselectInputFieldData = foundOption || null;
                    }
                } else if (objectType === 'user') {
                    const response = await getAllUsers();

                    if (response && response.map) {
                        const { map: userMap } = response;

                        this.multiselectInputFieldOptions = Object.entries(userMap).map(([value, label]) => ({
                            value,
                            label,
                        }));

                        if (this.ruleValue.value && this.multiselectInputFieldOptions.length > 0) {
                            this.multiselectInputFieldData = this.multiselectInputFieldOptions.find((v) => v.value === this.ruleValue.value) || null;
                        }
                    } else {
                        this.multiselectInputFieldOptions = [];
                    }
                } else if (this.ruleValue.value && this.multiselectInputFieldOptions.length > 0) {
                    this.multiselectInputFieldData = this.multiselectInputFieldOptions.find((v) => v.value === this.ruleValue.value) || null;
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>

.input-field, .date-picker, .multiselect {
    margin-bottom: 0;
}

.dd-rule-value-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: $spacing-125;
    min-width: 0;
}

.may-or-and-wrapper {
    display: flex;
    flex: 0 0 auto;
    flex-direction: row;
    align-items: center;
    gap: $spacing-125;
    margin-left: $spacing-125;
}

.may-or-label, .may-and-label {
    font-weight: bold;
    color: $color-blue;
    padding: 0;
    white-space: nowrap;
    cursor: pointer;
}

.comparison-label {
    flex: 0 0 auto;
    padding: 0;
    text-transform: lowercase;
}

.icon-x-delete-rule-value-input {
    cursor: pointer;
}

.ds-datepicker-wrapper, .ds-input-field-wrapper {
    flex: 0 1 px-to-rem(180);
    min-width: px-to-rem(120);
    margin-right: 0;
}

.ds-multiselect-wrapper {
    width: 100%;
    min-width: 0;

    .group-label {
        font-weight: bold;
    }

    .option-label {
        margin-left: $spacing-125;
    }
}

.multiselect-with-alert {
    display: flex;
    flex: 0 1 px-to-rem(180);
    flex-direction: column;
    align-items: flex-start;
    align-self: flex-start;
    min-width: px-to-rem(120);
}

.tag-alert {
    margin-top: $spacing-050;
    max-width: 100%;
}
</style>

<style lang="scss">
.dd-rule-value-row {
    .dropdown-menu {
        min-width: 150%;
    }

    .ds-datepicker-wrapper .date-picker-container > label {
        padding-top: 0;
    }
}
</style>
